import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
} from "../utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RiskMerchValue {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  archived_at: string | null;
  customer_id: string | null;
  market_zone_id: string;
  commodity: string;
  crop_year: number;
  note: string | null;
  value: string | null;
  range_start: string;
  range_end: string;
  Customer: { id: string; long_name: string } | null;
  MarketZone: { id: string; dtn_market_zone: string } | null;
}

export interface CreateMerchValuePayload {
  market_zone_id: string;
  commodity: string;
  crop_year: number;
  note?: string | null;
  value?: string | null;
  range_start: string;
  range_end: string;
}

export interface UpdateMerchValuePayload {
  note?: string | null;
  value?: string | null;
  range_start: string;
  range_end: string;
}

export interface ListMerchValuesQuery {
  customer_id?: string;
  market_zone_id?: string;
  commodity?: string;
  crop_year?: number;
  archived?: "true" | "false";
  limit?: number;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useListMerchValues(query?: ListMerchValuesQuery) {
  const queryParams = new URLSearchParams();

  if (query) {
    if (query.customer_id) {
      queryParams.set("customer_id", query.customer_id);
    }
    if (query.market_zone_id) {
      queryParams.set("market_zone_id", query.market_zone_id);
    }
    if (query.commodity) {
      queryParams.set("commodity", query.commodity);
    }
    if (query.crop_year !== undefined) {
      queryParams.set("crop_year", query.crop_year.toString());
    }
    if (query.archived !== undefined) {
      queryParams.set("archived", query.archived);
    }
    if (query.limit !== undefined) {
      queryParams.set("limit", query.limit.toString());
    }
  }

  const isArchived = query?.archived === "true";

  return useQuery({
    queryKey: ["risk", "merch-values", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<RiskMerchValue[]>("risk/merch-values", queryParams),
    retry: 2,
    enabled: isArchived ? true : !!query?.crop_year,
  });
}

export function useGetMerchValue(id: string) {
  return useQuery({
    queryKey: ["risk", "merch-values", id],
    queryFn: () =>
      fetchApiGet<RiskMerchValue>(`risk/merch-values/${id}`),
    enabled: !!id,
    retry: 2,
  });
}

export function useCreateMerchValue() {
  const queryClient = useQueryClient();

  return async (data: CreateMerchValuePayload) => {
    const result = await fetchApiPost<RiskMerchValue>(
      "risk/merch-values",
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "merch-values"] });
    }

    return result;
  };
}

export function useUpdateMerchValue() {
  const queryClient = useQueryClient();

  return async (id: string, data: UpdateMerchValuePayload) => {
    const result = await fetchApiPut<RiskMerchValue>(
      `risk/merch-values/${id}`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "merch-values"] });
    }

    return result;
  };
}

export function useDeleteMerchValue() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const result = await fetchApiDelete<RiskMerchValue>(
      `risk/merch-values/${id}`,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "merch-values"] });
    }

    return result;
  };
}

export function useArchiveMerchValue() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const result = await fetchApiPut<RiskMerchValue>(
      `risk/merch-values/${id}/archive`,
      {},
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "merch-values"] });
    }

    return result;
  };
}
